import { useState } from "react";
import { useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { RangeBar, RefreshButton } from "../../components/dashboard";
import { DashBar, DashTools } from "../../components/dx";
import { BarChart3, ChevronLeft } from "../../components/icons";
import { getCurrentRole } from "../../lib/auth";
import { ROLE_LABEL_LO } from "../../lib/roles";
import { breakdownCsv, downloadCsv } from "../../lib/dx";
import { seesFinanceStats } from "../../lib/permissions";
import { dateTimeLao } from "../../lib/format";
import {
  fetchBreakdown, placeFromParams, placeKey, placeToParams, rangeText, useOverviewData, type PlaceFilter,
} from "../../lib/dashboard-data";
import { rangeFromParams, rangeKey, rangeLabel, rangeToParams, type Range } from "../../lib/stats-range";
import type { StatsBreakdownRow } from "../../lib/api";
import { CHILD_WORD, placeTitle, useScopePath, type PathNode } from "./scope";
import { OverviewTab } from "./OverviewTab";
import { StatsTab } from "./StatsTab";
import { RevenueTab } from "./RevenueTab";
import { PopulationTab } from "./PopulationTab";
import { ReportPreview } from "./ReportPreview";
import type { DashCtx } from "./ctx";

const TABS = [
  { id: "overview", label: "ພາບລວມ" },
  { id: "stats", label: "ສະຖິຕິ" },
  { id: "revenue", label: "ລາຍຮັບ" },
  { id: "population", label: "ປະຊາກອນ" },
] as const;
type TabId = (typeof TABS)[number]["id"];

/**
 * Dashboard & ລາຍງານ — จอเดียวทุกระดับ (SDS-Admin-Mockup v3) แทน ພາບລວມເມືອງ + ພາບລວມທົ່ວແຂວງ เดิม
 *
 * เริ่มที่ขอบเขตของบัญชี (ผู้ดูแลระบบ = ทั่วประเทศ, แขวง = แขวงตัวเอง, เมือง = เมืองตัวเอง) แล้วเจาะลง
 * ແຂວງ → ເມືອງ → ຕາແສງ → ບ້ານ ผ่าน URL (`province`/`district`/`tasaeng`/`village`) — server ตรวจ scope ทุกครั้ง
 * นอกเขต = 403 · แท็บกับช่วงเวลาอยู่ใน URL เหมือนกัน แชร์ลิงก์แล้วเห็นชุดเดียวกัน
 */
export function DashboardPage() {
  const role = getCurrentRole();
  const [params, setParams] = useSearchParams();
  const range = rangeFromParams(params);
  const place = placeFromParams(params);
  const finance = seesFinanceStats();
  // ແຖບ ລາຍຮັບ ສະເພາະຜູ້ມີສິດເບິ່ງການເງິນ (DSH-04) — ລິ້ງເກົ່າທີ່ມີ tab=revenue ກັບໄປ ພາບລວມ
  const tabs = TABS.filter((t) => finance || t.id !== "revenue");
  const tabParam = params.get("tab");
  const tab: TabId = tabs.some((t) => t.id === tabParam) ? (tabParam as TabId) : "overview";
  const [preview, setPreview] = useState(false);

  const data = useOverviewData(range, place);
  const breakdown = useQuery({
    queryKey: ["stats", "breakdown", `${rangeKey(range)}|${placeKey(place)}`],
    queryFn: () => fetchBreakdown(range, place),
  });
  const { nodes, here } = useScopePath(range, place);

  const level = breakdown.data?.level ?? "none";
  const word = CHILD_WORD[level];
  const childLevel = level === "none" ? null : level;
  const rows = breakdown.data?.items ?? [];
  const nullLabel = word ? `ບໍ່ລະບຸ${word}` : "ບໍ່ລະບຸ";
  const nameOf = (r: StatsBreakdownRow) => (r.place_id && childLevel ? placeTitle(childLevel, r.name_lo) : nullLabel);
  const period = rangeText(range, data.daily.data ?? []);

  function go(next: { range?: Range; place?: PlaceFilter; tab?: TabId }) {
    const t = next.tab ?? tab;
    setParams(
      {
        ...rangeToParams(next.range ?? range),
        ...placeToParams(next.place ?? place),
        ...(t === "overview" ? {} : { tab: t }),
      },
      { replace: !next.place },
    );
  }
  // เจาะลง = ตัวกรองของขั้นนี้ + id ของพื้นที่ลูก (ผู้ดูแลระบบเริ่มจากทั่วประเทศ → แขวง)
  const onDrill = childLevel ? (id: string) => go({ place: { ...here.filter, [childLevel]: id } }) : undefined;
  const parent: PathNode | undefined = nodes.length > 1 ? nodes[nodes.length - 2] : undefined;

  const ctx: DashCtx = {
    range,
    place,
    here,
    period,
    data,
    breakdown,
    level,
    unit: childLevel ? { rows, word, nameOf, onDrill, today: range.kind === "today", finance } : undefined,
    finance,
  };

  function exportCsv() {
    const csv = breakdownCsv(rows, nameOf, `ລວມ ${here.name}`, data.summary.data, finance);
    downloadCsv(`ສະຫຼຸບ-${here.name}-${rangeLabel(range)}.csv`.replace(/[\s/()]+/g, "_"), csv.head, csv.rows);
  }

  return (
    <div className="dash dx">
      <nav className="dx-crumbs no-print" aria-label="ຂອບເຂດ">
        {nodes.map((n, i) => (
          <span key={`${n.level}-${n.id ?? "root"}`} className="dx-crumbs__item">
            {i > 0 ? <span className="dx-crumbs__sep">›</span> : null}
            {n.id && n.siblings.length > 1 ? (
              // ชื่อขั้นนี้เป็นตัวเลือก: สลับไปแขวง/เมือง/ตาแสงอื่นในขั้นแม่เดียวกัน (ระดับที่ลึกกว่าถูกล้าง)
              <select
                className={i === nodes.length - 1 ? "dx-crumbs__switch is-here" : "dx-crumbs__switch"}
                aria-label={`ປ່ຽນ${n.name}`}
                value={n.id}
                onChange={(e) => go({ place: { ...n.parentFilter, [n.level]: e.target.value } })}
              >
                {n.siblings.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            ) : i === nodes.length - 1 ? (
              <span className="dx-crumbs__here">{n.name}</span>
            ) : (
              <button type="button" className="dx-crumbs__link" onClick={() => go({ place: n.filter })}>
                {n.name}
              </button>
            )}
          </span>
        ))}
        {onDrill && rows.some((r) => r.place_id) ? (
          <select
            className="filter-select dx-crumbs__pick"
            aria-label={`ເບິ່ງລົງໄປຮອດ${word}`}
            value=""
            onChange={(e) => {
              if (e.target.value) onDrill(e.target.value);
            }}
          >
            <option value="">ເບິ່ງລົງໄປຮອດ{word}</option>
            {rows
              .filter((r) => r.place_id)
              .sort((a, b) => a.name_lo.localeCompare(b.name_lo, "lo"))
              .map((r) => (
                <option key={r.place_id} value={r.place_id ?? ""}>
                  {nameOf(r)}
                </option>
              ))}
          </select>
        ) : null}
        <span className="dx-crumbs__spacer" />
        {role ? <span className="role-pill">{ROLE_LABEL_LO[role]}</span> : null}
      </nav>

      <div className="dx-tabs no-print">
        <div className="dx-tabs__kicker">
          <BarChart3 size={14} /> Dashboard & ລາຍງານ
        </div>
        <div className="seg" role="tablist" aria-label="ມຸມມອງ">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={tab === t.id ? "seg__btn is-active" : "seg__btn"}
              onClick={() => go({ tab: t.id })}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <header className="dx-head">
        <div>
          <h1 className="dx-head__title">
            {TABS.find((t) => t.id === tab)?.label}
            {here.name}
          </h1>
          <p className="dx-head__sub">
            {here.name} · {period}
            {data.summary.dataUpdatedAt ? (
              <>
                {" "}
                · ອັບເດດ{" "}
                <span className="num">
{dateTimeLao(data.summary.dataUpdatedAt)}
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="row no-print">
          {parent ? (
            <button type="button" className="btn-secondary" onClick={() => go({ place: parent.filter })}>
              <ChevronLeft size={14} /> ກັບໄປ {parent.name}
            </button>
          ) : null}
          <RefreshButton updatedAt={data.summary.dataUpdatedAt} busy={data.summary.isFetching || breakdown.isFetching} />
        </div>
      </header>

      <DashBar
        tools={
          tab === "population" ? undefined : (
            <DashTools onPreview={() => setPreview(true)} onCsv={rows.length > 0 ? exportCsv : undefined} />
          )
        }
      >
        <RangeBar range={range} text={period} onChange={(r) => go({ range: r })} />
      </DashBar>
      {range.kind === "ytd" ? <p className="dx-cap">ນັບແຕ່ 01/01 ຂອງປີນີ້ ຮອດປັດຈຸບັນ</p> : null}

      {tab === "overview" ? <OverviewTab ctx={ctx} /> : null}
      {tab === "stats" ? <StatsTab ctx={ctx} /> : null}
      {tab === "revenue" ? <RevenueTab ctx={ctx} /> : null}
      {tab === "population" ? <PopulationTab ctx={ctx} /> : null}

      {preview ? <ReportPreview ctx={ctx} onClose={() => setPreview(false)} /> : null}
    </div>
  );
}
